import { Fragment } from "react"

import { cn } from "@/shared/utils/cn"

import type { YasalBelge, YasalBlok } from "../types"

const BOSLUK = "[●]"

/** Doldurulmamış `[●]` alanlarını ekranda fark edilir kılar. */
function Metin({ children }: { children: string }) {
  if (!children.includes(BOSLUK)) return children
  return children.split(BOSLUK).map((parca, i) => (
    <Fragment key={i}>
      {i > 0 && (
        <mark className="rounded-sm bg-amber-200/70 px-0.5 text-amber-950 dark:bg-amber-500/30 dark:text-amber-100">
          {BOSLUK}
        </mark>
      )}
      {parca}
    </Fragment>
  ))
}

function Blok({ blok }: { blok: YasalBlok }) {
  switch (blok.tip) {
    case "paragraf":
      return <p><Metin>{blok.metin}</Metin></p>
    case "fikra":
      return (
        <p>
          <strong className="me-1 tabular-nums">{blok.no}</strong>
          <Metin>{blok.metin}</Metin>
        </p>
      )
    case "liste":
      return (
        <ul className="list-disc space-y-1 ps-5">
          {blok.maddeler.map((madde) => (
            <li key={madde}><Metin>{madde}</Metin></li>
          ))}
        </ul>
      )
    case "tablo":
      return (
        <div className="overflow-x-auto rounded-md border print:overflow-visible">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50">
              <tr>
                {blok.basliklar.map((baslik) => (
                  <th key={baslik} className="px-3 py-2 font-medium">{baslik}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blok.satirlar.map((satir) => (
                <tr key={satir.join("|")} className="border-t align-top">
                  {satir.map((hucre, i) => (
                    <td key={i} className="px-3 py-2"><Metin>{hucre}</Metin></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
  }
}

function tarihMetni(iso: string) {
  const [yil, ay, gun] = iso.split("-")
  return `${gun}.${ay}.${yil}`
}

interface YasalBelgeIcerikProps {
  belge: YasalBelge
  /** Dialog içinde başlık zaten `DialogTitle`'da durduğu için gizlenir. */
  baslikGizle?: boolean
  className?: string
}

export function YasalBelgeIcerik({ belge, baslikGizle, className }: YasalBelgeIcerikProps) {
  return (
    <article className={cn("space-y-6 text-sm leading-relaxed", className)}>
      {!baslikGizle && (
        <header className="space-y-1 text-center">
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{belge.baslik}</h1>
          {belge.altBaslik && <p className="text-muted-foreground">{belge.altBaslik}</p>}
        </header>
      )}

      {belge.giris?.map((blok, i) => <Blok key={i} blok={blok} />)}

      {belge.bolumler.map((bolum) => (
        <section key={bolum.baslik} className="space-y-3">
          <h2 className="text-base font-semibold break-after-avoid">{bolum.baslik}</h2>
          {bolum.bloklar.map((blok, i) => <Blok key={i} blok={blok} />)}
        </section>
      ))}

      <p className="text-xs text-muted-foreground">
        Sürüm {belge.versiyon} · Son güncelleme {tarihMetni(belge.tarih)}
      </p>
    </article>
  )
}
